import {
  ADMIN_PAGE_LABELS,
  STAFF_PAGE_LABELS,
  STAFF_TAB_DISPLAY,
  STAFF_SELF_VIEW_LABEL,
  type StaffTab,
  type ActivityWindow,
  type ActivityFeedEvent,
} from '@bbbk/schema';

// The nav sections a feed row can navigate to — the admin page-label contract,
// so a new section becomes reachable from the feed with no edit here.
const NAV_VIEWS: ReadonlySet<string> = new Set(ADMIN_PAGE_LABELS);

// Shared formatting + section metadata for the Account ▸ Activity surfaces.
// Pure functions only, so they unit-test without a DOM.

// The nav sections the heatmap charts, in column order. Keys are the page_view
// labels the client records (routeMap keys); labels are the human names. Kept in
// one place so the heatmap columns and any section lookup can't drift.
// `staff-self-view` is the roster page a `staff` user lands on at / — its own
// column so their engagement shows alongside the admin-app sections.
// `short` is the compact x-axis label for the section bar chart (the full label
// reads in the tooltip), so seven categories fit a narrow stat card.
// The self-view's heatmap column key — also the label historic rows carry
// (the pre-AC10 recorder logged the whole page under this one label).
const SELF_VIEW_KEY = STAFF_SELF_VIEW_LABEL;
const SELF_VIEW_NAME = 'My Shifts';

export const SECTIONS: ReadonlyArray<{ key: string; label: string; short: string }> = [
  { key: 'today', label: 'Daily', short: 'Daily' },
  { key: 'bookings', label: 'Bookings', short: 'Book' },
  { key: 'dogs', label: 'Dogs & Owners', short: 'Dogs' },
  { key: 'schedule', label: 'Rota', short: 'Rota' },
  { key: 'finances', label: 'Finances', short: 'Fin' },
  { key: 'messages', label: 'Messages', short: 'Msgs' },
  { key: SELF_VIEW_KEY, label: SELF_VIEW_NAME, short: 'Shifts' },
];

// Recorded labels FINER than a heatmap column: every staff tab label (the
// STAFF_PAGE_LABELS contract) rolls into the one self-view column, while
// "Last section" and the feed keep the per-tab distinction. Derived from the
// contract, so a new staff tab lands in the right column with no edit here.
const SECTION_ROLLUP: Record<string, string> = Object.fromEntries(
  Object.values(STAFF_PAGE_LABELS).map((label) => [label, SELF_VIEW_KEY]),
);

// A recorded page_view label → its heatmap column key (identity for labels
// that ARE a column). One home, shared by the section bar and any grid join.
export const sectionKey = (label: string): string => SECTION_ROLLUP[label] ?? label;

const SECTION_LABEL: Record<string, string> = {
  ...Object.fromEntries(SECTIONS.map((s) => [s.key, s.label])),
  account: 'Account', // an admin page, not a heatmap column — but named for the "Last section" lookup
  // Per-tab display names: 'My Shifts · Today' / 'My Shifts · Day after'. The
  // human name comes from STAFF_TAB_DISPLAY, never from capitalising the key —
  // keys are wire/route tokens ('day-after') and read wrong capitalised.
  ...Object.fromEntries(
    Object.entries(STAFF_PAGE_LABELS).map(([tab, label]) => [
      label,
      `${SELF_VIEW_NAME} · ${STAFF_TAB_DISPLAY[tab as StaffTab]}`,
    ]),
  ),
};

// The join key for matching an `actor_email` (citext in the DB, any casing) to
// an allowlist email on the client: lowercased, so a mixed-case identity provider
// can't split one person into two. One home for the rule — used by the People
// column merge and the heatmap join.
export const emailKey = (email: string): string => email.toLowerCase();

// A page_view key → its human label (falls back to the raw key for anything new).
export function sectionLabel(key: string | null): string {
  if (!key) return '—';
  return SECTION_LABEL[key] ?? key;
}

// Compact dwell for a feed page-view row: "40s" under a minute, "3 min" after.
// Null/zero (no dwell recorded) renders as nothing — the caller omits the suffix.
export function dwellLabel(ms: number | null): string | null {
  if (!ms || ms <= 0) return null;
  const sec = Math.round(ms / 1000);
  if (sec < 1) return null;
  if (sec < 60) return `${sec}s`;
  return `${Math.round(sec / 60)} min`;
}

// A page_view feed row's copy, shared by the everyone feed and the per-user
// rail so the two can never phrase the same event differently. `dwell` is
// separate so a renderer can style it muted.
export function pageViewRow(e: Pick<ActivityFeedEvent, 'label' | 'durationMs'>): { text: string; dwell: string | null } {
  return { text: `viewed ${sectionLabel(e.label)}`, dwell: dwellLabel(e.durationMs) };
}

// --- The action vocabulary, in English ---------------------------------------
// `booking.update` is the wire format the middleware records, not something to
// put on screen. Page-view rows already read as English (pageViewRow above);
// this gives actions the same treatment, so the ledger carries no wire tokens.
//
// The vocabulary is a CLOSED set generated by one regex in the API's
// log-actions.ts, so this map is finite. Two general maps compose the common
// `<entity>.<verb>` shape; the handful that don't read literally are spelled out.

const ACTION_VERB: Record<string, string> = {
  create: 'created',
  update: 'updated',
  delete: 'deleted',
  // Its own verb server-side — without it an un-archive would be
  // indistinguishable from a create.
  restore: 'restored',
};

const ACTION_ENTITY: Record<string, string> = {
  booking: 'a booking',
  owner: 'an owner',
  dog: 'a dog',
  shift: 'a shift',
  holiday: 'a holiday',
  // A person, not a record — "updated a staff" reads wrong.
  staff: 'a staff member',
  viewing: 'a viewing',
};

// Actions whose `<entity>.<verb>` shape does not read literally: pay-mark.update
// is not "updated a pay-mark", it is marking a week paid. The account grants and
// revokes are the security-relevant ones, so they get plain words.
const ACTION_PHRASE: Record<string, string> = {
  'pay-mark.update': 'marked a week paid',
  // DELETE /api/pay-marks un-marks a paid week. It is a collection-level route,
  // so actionFor records `pay-mark.delete` — which fell through to the raw wire
  // token, since `pay-mark` has no entry in ACTION_ENTITY (Codex, PR #149).
  'pay-mark.delete': 'unmarked a week as paid',
  // A payment PATCH. `update` corrects a recorded amount; `clear` voids the row
  // outright, which is the one that takes money back off a booking's balance. An
  // audit unable to tell those apart would not be much of an audit.
  'payment.update': 'corrected a payment',
  'payment.clear': 'cleared a payment',
  'account.grant': 'granted access',
  'account.revoke': 'revoked access',
  'setting.update': 'changed a setting',
  'staff.create': 'added a staff member',
  // The deposit-proof screenshots (PR #255 review): a customer sends one, the
  // desk accepts, refuses or removes it.
  'proof.upload': 'sent a payment screenshot',
  'proof.accept': 'accepted a payment screenshot',
  'proof.reject': 'asked for a new payment screenshot',
  'proof.remove': 'removed a payment screenshot',
  // A dog's vaccination card (vaccination-cards AC12): sent by the customer or added by the
  // desk, and the desk's three answers on it.
  'card.upload': 'added a vaccination card',
  'card.accept': 'accepted a vaccination card',
  'card.reject': 'didn’t accept a vaccination card',
  'card.undo': 'undid an answer on a vaccination card',
  'card.remove': 'removed a vaccination card',
  // Restricted drop-off and collection days, on Account › Customer portal (PR #256 review).
  'handover-day.create': 'restricted a drop-off and collection day',
  'handover-day.delete': 'removed a restricted day',
};

// An action's recorded label → a lower-case sentence fragment, so it reads on
// from the run head's name ("Aoife … updated a booking") and matches the
// phrasing the per-user rail already uses for page views.
//
// An UNKNOWN label returns AS ITSELF rather than a guess: a new entity in
// log-actions.ts then surfaces as a visible raw token, which is the signal to
// add it here. Inventing a phrase would hide that gap instead of showing it.
export function actionText(label: string | null): string {
  if (!label) return 'made a change';
  const phrase = ACTION_PHRASE[label];
  if (phrase) return phrase;
  const [entity, verb] = label.split('.');
  const v = ACTION_VERB[verb ?? ''];
  const e = ACTION_ENTITY[entity ?? ''];
  return v && e ? `${v} ${e}` : label;
}

// An actor's email → the name the ledger's run head shows. The full address
// stays on the head's title, so nothing is hidden — this is just the part worth
// repeating. Separators become spaces so 'freddi.bernon' reads as a name rather
// than a token.
export function personName(email: string): string {
  const local = email.split('@')[0] ?? email;
  return local
    .split(/[._-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

// --- Runs: consecutive events by one person ----------------------------------
// The ledger's unit (docs/design/activity-feed-rethink, direction A). Four rows
// for eleven minutes of one person becomes one head plus four quiet lines, so
// identity is stated once rather than once per row.
export interface ActivityRun {
  actor: string; // the actor's email, in the casing the run's newest event carried
  events: ActivityFeedEvent[]; // newest first, as received
  from: string; // ISO of the run's EARLIEST event
  to: string; // ISO of the run's LATEST event
}

// Group an already-sorted (newest-first) list into consecutive same-actor runs,
// preserving order. A person returning later in the day after someone else
// starts a NEW run — merging them would claim one continuous stretch that never
// happened. Matching is on emailKey, so a citext address in mixed case cannot
// split one person into two runs.
export function groupIntoRuns(events: ActivityFeedEvent[]): ActivityRun[] {
  const out: ActivityRun[] = [];
  for (const e of events) {
    const last = out[out.length - 1];
    if (last && emailKey(last.actor) === emailKey(e.actor)) {
      last.events.push(e);
      last.from = e.at; // newest-first, so each later row carries the earlier time
    } else {
      out.push({ actor: e.actor, events: [e], from: e.at, to: e.at });
    }
  }
  return out;
}

// The page someone landed on after signing in, or null when it can't be known.
//
// A sign-in was the one row that said nothing about where the person went, and
// the answer is already in the stream: AppShell records a page view when the
// view CHANGES, i.e. on LEAVING a page — so the first page_view after a login is
// the page they landed on, timestamped at the moment they left it.
//
// `events` is NEWEST-first, so an event later in time sits at a LOWER index;
// the scan therefore walks downward from the login. It stops at an intervening
// login (that session's landing page belongs to the newer sign-in, not this
// one), and returns null rather than reaching backwards past the login — a page
// view BEFORE a sign-in belongs to an earlier session, and claiming it would be
// a fabrication. Null is also the honest answer when the session's first view
// simply isn't on this page of the feed.
export function landingSection(events: ActivityFeedEvent[], loginIndex: number): string | null {
  const login = events[loginIndex];
  if (!login) return null;
  // Recorded on the event itself since 2026-07-26 — authoritative, and the only
  // thing that works for a session that never navigated away. Older rows fall
  // through to the inference below.
  if (login.label) return sectionLabel(login.label);

  // The fallback, for rows recorded before logins carried their own label. It
  // takes the whole DAY's events, not one person's run: a run ends the moment
  // another actor's event interleaves, so a login by A followed by B's event and
  // then A's own page view could never reach it (Claude, PR #149). Other actors'
  // events are therefore skipped rather than ending the scan.
  //
  // Caveat this cannot fix, and does not pretend to: page views carry no session
  // id, so with two tabs open for one account the next page view may belong to
  // the other tab (Codex). That is why recording the label at sign-in replaced
  // inference rather than supplementing it — this path only serves historic rows.
  const who = emailKey(login.actor);
  for (let i = loginIndex - 1; i >= 0; i--) {
    const e = events[i]!;
    if (emailKey(e.actor) !== who) continue; // someone else's event — not this session
    if (e.kind === 'login') return null; // a later sign-in owns everything after it
    if (e.kind === 'page_view' && e.label) return sectionLabel(e.label);
  }
  return null;
}

// --- Where a feed row leads --------------------------------------------------
// Freddi 2026-07-26: "i want to be able to click on the activity feed item and
// it should bring me directly to that page, booking, update etc."
//
// Resolved as DATA, not as a click handler, so the mapping unit-tests without a
// DOM and one place decides what is reachable. `null` means the row is inert —
// which is the honest answer more often than it looks:
//   - a DELETED record cannot be opened, so a `.delete` action goes to the
//     section instead of a modal that would 404;
//   - a dog has no standalone modal (RecordModalProvider opens its household,
//     and the feed only carries the dog id), so it goes to Dogs & Owners;
//   - a staff self-view page belongs to that person's own screen, which an
//     admin has no route to;
//   - `account.revoke` names someone who no longer has access to open.
export type FeedDestination =
  | { kind: 'section'; view: string }
  | { kind: 'booking'; id: string }
  | { kind: 'owner'; id: string }
  | { kind: 'helper'; id: string }
  // `id` is the helper whose leave view opens; `holidayId` is the entry to land
  // ON. Without the second the modal opened the right person's leave list with
  // nothing selected — naming a holiday and showing none (Codex, PR #149).
  | { kind: 'helperLeave'; id: string; holidayId?: string }
  | null;

// Which nav section each action entity lives on — the fallback when the exact
// record cannot be opened (it was deleted, or its parent no longer resolves).
const ENTITY_SECTION: Record<string, string> = {
  booking: 'bookings',
  owner: 'dogs',
  dog: 'dogs',
  shift: 'schedule',
  holiday: 'schedule',
  staff: 'schedule',
  // Pay marks are toggled in the Rota's week money strip (components/staff/
  // RotaView + usePayMarks), NOT on Finances — which carries revenue analytics
  // and no control for inspecting or undoing a mark (Codex, PR #149).
  'pay-mark': 'schedule',
  account: 'account',
  setting: 'account',
  'handover-day': 'account',
  // A viewing lives on the Daily desk and has no list of its own — it is
  // deliberately absent from Bookings (docs/out-of-scope/viewings.md). Without
  // an entry here every viewing row in the feed rendered inert, unlike every
  // other operational action (codex, PR #172). The section is the honest
  // destination: it is where the record can actually be found.
  viewing: 'today',
};

// The editor each action entity opens, and which id opens it (Freddi
// 2026-07-26: "any changes to a booking should open the edit booking modal,
// any changes to a household should open the edit household modal, any changes
// to helpers should open edit helper etc and apply accordingly to every action").
//
// `via: 'target'` — the affected record IS the one you edit, so its own id opens
// the modal. `via: 'parent'` — the affected record has no editor of its own and
// is edited through something else, so the feed's server-resolved `targetParent`
// opens it: a dog through its household, a shift or a holiday through its helper
// (a holiday opening straight into that helper's leave view, since that is where
// leave is edited).
// `afterDelete` is per-entity because DELETE means different things here, and a
// blanket exclusion was wrong in both directions (Codex, PR #149):
//   'section' — nothing survives worth opening.
//   'open'    — the record (or its parent) is still there and still editable.
const ENTITY_EDITOR: Record<
  string,
  { kind: 'booking' | 'owner' | 'helper' | 'helperLeave'; via: 'target' | 'parent'; afterDelete: 'open' | 'section' }
> = {
  // Hard-deleted (`DELETE FROM bookings`) — the row is genuinely gone.
  booking: { kind: 'booking', via: 'target', afterDelete: 'section' },
  // ARCHIVED, not removed: `archived_at = now()`, with a /restore endpoint, and
  // fetchOwnerDetail loads it regardless of archived state. So a delete still
  // opens the household — which is where you would go to undo it.
  owner: { kind: 'owner', via: 'target', afterDelete: 'open' },
  // Archived too, but GET /api/staff filters `archived_at IS NULL`, so the modal
  // never receives the helper and renders a blank NEW-helper form instead. Worse
  // than useless, so a deleted helper goes to the section.
  staff: { kind: 'helper', via: 'target', afterDelete: 'section' },
  // Via-parent entities are self-correcting on delete: the parent is resolved by
  // a server-side join, so if the row is gone there is no parent and the
  // destination falls back to the section on its own.
  dog: { kind: 'owner', via: 'parent', afterDelete: 'open' },
  shift: { kind: 'helper', via: 'parent', afterDelete: 'open' },
  holiday: { kind: 'helperLeave', via: 'parent', afterDelete: 'open' },
};

export function feedDestination(
  e: Pick<ActivityFeedEvent, 'kind' | 'label' | 'target' | 'targetParent'>,
): FeedDestination {
  if (e.kind === 'page_view' || e.kind === 'login') {
    // Both carry a page label. Staff self-view pages are that person's own
    // screen — an admin has no route to it, so those stay inert.
    const key = e.label ? sectionKey(e.label) : null;
    if (!key || key === SELF_VIEW_KEY) return null;
    return NAV_VIEWS.has(key) ? { kind: 'section', view: key } : null;
  }
  if (e.kind !== 'action' || !e.label) return null;
  const [entity, verb] = e.label.split('.');
  if (!entity) return null;

  const editor = ENTITY_EDITOR[entity];
  if (editor && (verb !== 'delete' || editor.afterDelete === 'open')) {
    const id = editor.via === 'target' ? e.target : e.targetParent;
    if (id) {
      // A holiday carries BOTH: the helper opens the leave view, the holiday id
      // selects the entry inside it.
      return editor.kind === 'helperLeave'
        ? { kind: 'helperLeave', id, holidayId: e.target ?? undefined }
        : { kind: editor.kind, id };
    }
  }
  // No editor reachable. The remaining actions genuinely have nothing to open:
  // `pay-mark.update` and `setting.update` carry no target at all;
  // `account.grant`'s email lives in the request body, which the middleware
  // never sees; and `account.revoke` names someone whose access has just been
  // removed, so there is no user left to edit. Each lands on its section.
  const view = ENTITY_SECTION[entity];
  return view ? { kind: 'section', view } : null;
}

// The period language for the Account surfaces, in ONE home so a card head, an
// empty state and a table footnote can never name different windows for the
// same read. Every activity surface is bounded (the endpoints take ?window=),
// but until this landed none of them said so on screen — and a bounded chart
// that never states its bound reads as a lifetime total. That was not a
// hypothetical: activity_events was created 2026-06-29, so on 2026-07-25 the
// 30-day window still covered every row ever recorded and the chart was
// genuinely indistinguishable from an all-time one.
export const WINDOW_LABEL: Record<ActivityWindow, string> = {
  week: 'Last 7 days',
  month: 'Last 30 days',
  all: 'All time',
};

// The same windows phrased to slot into an empty-state sentence — "No sign-ins
// <phrase>." A naive lowercase of WINDOW_LABEL would render the ungrammatical
// "No sign-ins in the all time", so the two forms are spelled out separately.
export const WINDOW_EMPTY_PHRASE: Record<ActivityWindow, string> = {
  week: 'in the last 7 days',
  month: 'in the last 30 days',
  all: 'yet',
};

// And a third form, for "<these columns> cover ___." Lower-casing WINDOW_LABEL
// there produced "cover the all time" — the same failure WINDOW_EMPTY_PHRASE
// exists to avoid, in the one sentence position that had been left reusing the
// label (Claude, PR #149). Unreachable today, since AccountPage fixes the window
// to 'month'; reachable the moment a window selector returns.
export const WINDOW_COVERAGE_PHRASE: Record<ActivityWindow, string> = {
  week: 'the last 7 days',
  month: 'the last 30 days',
  all: 'all time',
};

// "2 min ago" / "3 h ago" / "yesterday" / "12 Jun" — approximate, compact.
// `now` is injectable so the relative output is deterministic under test.
export function relTime(iso: string | null, now: number = Date.now()): string {
  if (!iso) return 'never';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return 'never';
  const sec = Math.max(0, Math.round((now - t) / 1000));
  if (sec < 60) return 'just now';
  const min = Math.round(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr} h ago`;
  const day = Math.round(hr / 24);
  if (day === 1) return 'yesterday';
  if (day < 7) return `${day} days ago`;
  return new Date(iso).toLocaleDateString('en-IE', { day: 'numeric', month: 'short' });
}

// "Today" / "Yesterday" / "Mon 12 Jun" — the feed's day-group heading for a date.
export function dayHeading(iso: string, now: number = Date.now()): string {
  const d = new Date(iso);
  const today = new Date(now);
  const startOf = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diffDays = Math.round((startOf(today) - startOf(d)) / 86_400_000);
  if (diffDays <= 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  return d.toLocaleDateString('en-IE', { weekday: 'short', day: 'numeric', month: 'short' });
}

// hh:mm for a feed row time.
export function timeOfDay(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-IE', { hour: '2-digit', minute: '2-digit', hour12: false });
}

// Group an already-sorted (newest-first) event list into [dayHeading, rows][],
// preserving order. Shared by the Activity feed (everyone) and the edit-user
// modal's per-user timeline, so the day banding is identical in both. `now` is
// injectable so the headings are deterministic under test.
export function groupByDay(events: ActivityFeedEvent[], now: number = Date.now()): [string, ActivityFeedEvent[]][] {
  const out: [string, ActivityFeedEvent[]][] = [];
  for (const e of events) {
    const day = dayHeading(e.at, now);
    const last = out[out.length - 1];
    if (last && last[0] === day) last[1].push(e);
    else out.push([day, [e]]);
  }
  return out;
}
